<template>
  <v-dialog
    v-model="showDialog"
    persistent
    max-width="520"
    transition="dialog-bottom-transition"
  >
    <v-card class="kyc-prompt-card" elevation="0">
      <!-- Header avec gradient -->
      <div class="kyc-prompt-header">
        <div class="kyc-prompt-glow">
          <v-icon color="white" size="36">mdi-shield-lock-outline</v-icon>
        </div>
        <v-btn
          icon
          small
          class="kyc-prompt-close"
          @click="dismiss"
        >
          <v-icon color="white" size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Body -->
      <v-card-text class="kyc-prompt-body">
        <h2 class="kyc-prompt-title">Unlock Full Access</h2>
        <p class="kyc-prompt-subtitle">
          Your account balance has reached <strong>{{ formatBalance }}</strong>. To continue using all features securely, identity verification is now required.
        </p>

        <!-- Pourquoi c'est nécessaire -->
        <div class="kyc-prompt-reasons">
          <div
            v-for="(reason, i) in reasons"
            :key="i"
            class="kyc-reason"
          >
            <div class="kyc-reason-icon">
              <v-icon :color="reason.color" size="20">{{ reason.icon }}</v-icon>
            </div>
            <div class="kyc-reason-text">
              <div class="kyc-reason-title">{{ reason.title }}</div>
              <div class="kyc-reason-desc">{{ reason.desc }}</div>
            </div>
          </div>
        </div>

        <!-- Warning -->
        <v-alert
          dense
          text
          type="warning"
          class="kyc-prompt-alert mt-4"
          icon="mdi-alert-circle-outline"
        >
          Without verification, certain features like withdrawals, Njangi participation, and loan applications will remain locked.
        </v-alert>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="kyc-prompt-actions">
        <v-btn
          text
          color="grey darken-1"
          class="text-none font-weight-medium"
          @click="dismiss"
        >
          Remind me later
        </v-btn>
        <v-btn
          color="primary"
          depressed
          rounded
          class="px-6 text-none font-weight-bold"
          @click="goToKYC"
        >
          <v-icon left small>mdi-shield-account-outline</v-icon>
          Verify My Identity
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'KYCPromptDialog',

  props: {
    // Solde actuel du user (passé depuis le parent ou récupéré du store)
    balance: {
      type: Number,
      default: 0,
    },
    // Seuil minimum pour afficher le prompt (défaut: 2000)
    threshold: {
      type: Number,
      default: 2000,
    },
    // Statut KYC du user (depuis le store ou parent)
    kycStatus: {
      type: String,
      default: 'none', // 'none', 'pending', 'approved', 'rejected'
    },
    // ID unique du user pour le localStorage
    userId: {
      type: [String, Number],
      default: 'anonymous',
    },
  },

  data() {
    return {
      showDialog: false,
      dismissed: false,
      reasons: [
        {
          icon: 'mdi-shield-check',
          color: 'green darken-2',
          title: 'Secure Your Account',
          desc: 'Protect your funds and prevent unauthorized access.',
        },
        {
          icon: 'mdi-handshake',
          color: 'purple darken-2',
          title: 'Access Njangi & Loans',
          desc: 'Join group savings and apply for credit products.',
        },
        {
          icon: 'mdi-account-check',
          color: 'orange darken-2',
          title: 'Build Trust',
          desc: 'Verified users get higher limits and priority support.',
        },
      ],
    }
  },

  computed: {
    // Vérifie si le solde dépasse le seuil
    hasEnoughBalance() {
      return this.balance >= this.threshold
    },

    // Vérifie si le KYC n'est pas déjà approuvé
    needsKYC() {
      return this.kycStatus !== 'approved'
    },

    // Formate le solde avec séparateur de milliers
    formatBalance() {
      return new Intl.NumberFormat('en-US').format(this.balance) + ' F CFA'
    },

    // Détermine si on doit afficher le dialog
    shouldShow() {
      // Flag réactif local (session courante)
      if (this.dismissed) return false

      // Ne pas couvrir la page produits recommandés (navigation depuis le chat)
      if (this.isRecommendedPage()) return false

      // Vérification via le store (version-based, comme Njangi/Loan)
      if (this.$store.getters.kycPromptSeenForCurrentUser) return false

      // Conditions métier
      return this.hasEnoughBalance && this.needsKYC
    },
  },

  watch: {
    '$route' () {
      if (this.isRecommendedPage() && this.showDialog) {
        this.showDialog = false
      }
    },
    // Ouvrir le dialog dès que shouldShow devient true
    shouldShow: {
      immediate: true,
      handler(val) {
        if (val && !this.showDialog) {
          setTimeout(() => {
            if (val && !this.isRecommendedPage()) {
              this.showDialog = true
            }
          }, 1200)
        }
      }
    }
  },

  methods: {
    isRecommendedPage() {
      return !!(this.$route && (
        this.$route.name === 'recommended_products' ||
        (this.$route.path || '').startsWith('/market_place/recommended/')
      ))
    },
    _markSeen() {
      this.dismissed = true
      const userId = this.$store.state.user_info?.user?.id || this.$store.state.username || 'anonymous'
      this.$store.commit('setKycPromptSeenForUser', { userId })
    },

    dismiss() {
      this.showDialog = false
      this._markSeen()
    },

    goToKYC() {
      this.showDialog = false
      this._markSeen()
      this.$router.push('/user_profile/identity')
    },
  },
}
</script>

<style scoped>
.kyc-prompt-card {
  border-radius: 24px;
  overflow: hidden;
  background: #ffffff;
}

/* Header */
.kyc-prompt-header {
  position: relative;
  height: 120px;
  background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.kyc-prompt-glow {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(13, 71, 161, 0.3);
}

.kyc-prompt-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* Body */
.kyc-prompt-body {
  padding: 28px 28px 16px;
  text-align: center;
}

.kyc-prompt-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #0d2b5e;
  margin: 0 0 8px;
  letter-spacing: -0.3px;
}

.kyc-prompt-subtitle {
  font-size: 0.9rem;
  color: #6b7c93;
  line-height: 1.6;
  margin: 0 0 24px;
}

.kyc-prompt-subtitle strong {
  color: #1976d2;
}

/* Reasons list */
.kyc-prompt-reasons {
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
}

.kyc-reason {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid rgba(23, 84, 180, 0.06);
  transition: all 0.2s ease;
}

.kyc-reason:hover {
  background: #f0f7ff;
  border-color: rgba(25, 118, 210, 0.12);
}

.kyc-reason-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.kyc-reason-text {
  flex: 1;
  min-width: 0;
}

.kyc-reason-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0d2b5e;
  margin-bottom: 2px;
}

.kyc-reason-desc {
  font-size: 0.82rem;
  color: #78909c;
  line-height: 1.4;
}

/* Alert */
.kyc-prompt-alert {
  border-radius: 12px !important;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Actions */
.kyc-prompt-actions {
  padding: 8px 24px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Responsive */
@media (max-width: 600px) {
  .kyc-prompt-body {
    padding: 20px 20px 12px;
  }
  .kyc-prompt-title {
    font-size: 1.15rem;
  }
  .kyc-reason {
    padding: 12px;
  }
  .kyc-prompt-actions {
    flex-direction: column-reverse;
    gap: 8px;
    padding: 8px 20px 20px;
  }
  .kyc-prompt-actions .v-btn {
    width: 100%;
  }
}
</style>